import { useEffect, useMemo } from 'react';
import { Caption, BasicHorizontalCard, WeSpinBadge } from '@comeon/ui';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrency } from '~/components/account/ducks/index.js';
import StartTime from '../components/start-time.js';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import {
	BEFORE_RUNNING_STATES,
	ENDED,
	CANCELLED,
	RUNNING,
	PENDING_START,
	BUY_IN_STATES,
	ANNOUNCED
} from '../constants/we-spin-stream-states.js';
import Icon, { ICON_RIGHT } from '~/components/icon/index.js';
import { fetchFinishGame } from '../ducks/we-spin-duck.js';
import { selectFinishGame } from '../ducks/we-spin-selectors.js';
import { getTagText } from '../constants/we-spin-boost-constants.js';
import generateSrcSet from '../../../../utils/generateSrcSet.js';
import {
	WE_SPIN_NEXT_SESSION_ITEM_DATA_AT,
	WE_SPIN_NEXT_SESSION_ITEM_CHEVRON_DATA_AT
} from '../constants/we-spin-data-at-attributes.js';
import EventTypeTitle from '../components/event-type-title.js';

const topTextColor = 'var(--we-spin-next-session-top-text-color,rgba(var(--gray-50), 1))';
const middleTextColor = 'var(--we-spin-next-session-middle-text-color, rgba(var(--primary), 1))';
const bottomTextColor = 'var(--we-spin-next-session-bottom-text-color,rgba(var(--gray-50), 1))';

const isBuyInOkay = (state, joined, isFreeWeSpin, isEventCapMet) => {
	return (
		(BEFORE_RUNNING_STATES.includes(state) && !isFreeWeSpin) ||
		(BEFORE_RUNNING_STATES.includes(state) && joined) ||
		(BEFORE_RUNNING_STATES.includes(state) && !joined && !isEventCapMet) ||
		(state === PENDING_START && !joined && !isEventCapMet) ||
		(state === PENDING_START && joined && isEventCapMet) ||
		(state === PENDING_START && isFreeWeSpin && !isEventCapMet)
	);
};

const WeSpinGameRoomNextSessionItem = ({
	stream,
	locale,
	showGameIcon,
	translations,
	onClick,
	showStartTime = true
}) => {
	const {
		currentGame,
		streamerImgUrl,
		streamBgUrl,
		streamRef,
		start,
		durationInMinutes,
		joined,
		state,
		balance,
		currencyCode,
		balanceCapMet,
		noParticipants,
		buyInAmount,
		boost,
		eventTags = [],
		playerCapMet,
		isFreeWeSpin
	} = stream;

	const playerCurrency = useSelector(selectCurrency);

	const dispatch = useDispatch();
	const checkFinished = state === ENDED && joined;

	useEffect(() => {
		if (checkFinished) {
			dispatch(fetchFinishGame(streamRef));
		}
	}, [dispatch, streamRef, checkFinished]);

	let finishedText;
	const finishGame = useSelector(selectFinishGame(streamRef));
	if (checkFinished && finishGame) {
		const { yourBalance } = finishGame;
		finishedText = `${translations.WE_SPIN_EVENT_LIST_PAYOUT}: ${yourBalance}`;
	}

	const hasEventTags = eventTags?.length > 0;

	const isEventCapMet = playerCapMet || balanceCapMet;
	const isEventDisabled = useMemo(
		() =>
			(isFreeWeSpin && (state === ANNOUNCED || (playerCapMet && !joined))) ||
			(!isFreeWeSpin && isEventCapMet && !joined) ||
			!BUY_IN_STATES.includes(state),
		[isFreeWeSpin, state, playerCapMet, joined, isEventCapMet]
	);

	const imageUrl = showGameIcon ? currentGame.gameImageUrl : streamBgUrl || streamerImgUrl;

	const imageSrcSet = showGameIcon
		? generateSrcSet(currentGame.gameImageUrl)
		: (streamBgUrl && generateSrcSet(streamBgUrl)) || (streamerImgUrl && generateSrcSet(streamerImgUrl));
	/*
	const imageUrl =
		streamBgUrl ||
		(showGameIcon ? currentGame.gameImageUrl : streamerImgUrl);

	const imageSrcSet = generateSrcSet(imageUrl);
*/
	const bottomText =
		BUY_IN_STATES.includes(state) && joined && !isFreeWeSpin
			? `${translations.WE_SPIN_LOBBY_CARD_BUTTON_YOUR_BUY_IN} ${currencyFormatter.format(
					buyInAmount,
					playerCurrency,
					locale
				)}`
			: BUY_IN_STATES.includes(state) && isFreeWeSpin && !joined && !isEventCapMet
				? `${translations.WE_SPIN_BUY_IN_FREE_WE_SPIN_JOIN} ${noParticipants} ${translations.WE_SPIN_NEXT_SESSION_PLAYERS}`
				: BUY_IN_STATES.includes(state) && isFreeWeSpin && joined
					? `${translations.WE_SPIN_YOU_ARE_REGISTERED}`
					: BUY_IN_STATES.includes(state) && isFreeWeSpin && isEventCapMet
						? `${translations.WE_SPIN_NEXT_SESSION_FULLY_BOOKED}`
						: BUY_IN_STATES.includes(state)
							? `${translations.WE_SPIN_NEXT_SESSION_GAME_BALANCE} ${currencyFormatter.format(
									balance,
									currencyCode,
									locale
								)}`
							: state === ENDED && joined
								? finishedText
								: state === CANCELLED
									? `${translations.WE_SPIN_EVENT_LIST_EVENT_CANCELLED}`
									: state === RUNNING
										? `${translations.WE_SPIN_EVENT_LIST_EVENT_RUNNING}`
										: `${translations.WE_SPIN_EVENT_LIST_EVENT_ENDED}`;

	const formatTime = time => `${time.getHours()}:${`00${time.getMinutes()}`.slice(-2)}`;

	const startDate = new Date(start);
	const endDate = new Date(startDate.getTime() + durationInMinutes * 60000);
	const formattedTime = `${formatTime(startDate)} - ${formatTime(endDate)}`;

	const topText = showStartTime ? <StartTime start={start} state={state} joined={joined} /> : formattedTime;
	const middleText =
		joined && !isFreeWeSpin
			? `${translations.WE_SPIN_NEXT_SESSION_JOINED} ${noParticipants} ${translations.WE_SPIN_NEXT_SESSION_PLAYERS}`
			: isFreeWeSpin && !joined
				? `${translations.WE_SPIN_NEXT_SESSION_JOIN_FOR_FREE}`
				: isFreeWeSpin && joined
					? `${translations.WE_SPIN_NEXT_SESSION_JOINED_FREE_WESPIN}`
					: `${translations.WE_SPIN_NEXT_SESSION_BUYIN_AND_JOIN}`;
	const middleTextSuffix =
		isEventCapMet && !joined && !isFreeWeSpin ? ` - ${translations.WE_SPIN_NEXT_SESSION_FULLY_BOOKED}` : '';

	if (isBuyInOkay(state, joined, isFreeWeSpin, isEventCapMet)) {
		return (
			<BasicHorizontalCard
				iconRight={<Icon type={ICON_RIGHT} />}
				disabled={isEventDisabled}
				dataTag={streamRef}
				dataAt={WE_SPIN_NEXT_SESSION_ITEM_DATA_AT}
				dataAtButton={WE_SPIN_NEXT_SESSION_ITEM_CHEVRON_DATA_AT}
				onClick={onClick}
				badge={boost && <WeSpinBadge>{getTagText(boost, locale)}</WeSpinBadge>}
				hasPrimaryButton
				topText={topText}
				topTextColor={topTextColor}
				middleText={`${middleText}${middleTextSuffix}`}
				middleTextColor={middleTextColor}
				bottomText={bottomText}
				bottomTextColor={bottomTextColor}
				imageSrc={!hasEventTags && imageUrl}
				imageSrcSet={!hasEventTags && imageSrcSet}
				imageComponent={hasEventTags && <EventTypeTitle eventType={eventTags[0]} translations={translations} />}
			/>
		);
	}
	return (
		<BasicHorizontalCard
			iconRight={<Icon type={ICON_RIGHT} />}
			disabled={isEventDisabled}
			dataTag={streamRef}
			dataAt={WE_SPIN_NEXT_SESSION_ITEM_DATA_AT}
			dataAtButton={WE_SPIN_NEXT_SESSION_ITEM_CHEVRON_DATA_AT}
			onClick={onClick}
			badge={boost && <WeSpinBadge>{getTagText(boost, locale)}</WeSpinBadge>}
			topText={topText}
			middleText={bottomText}
			middleTextColor={middleTextColor}
			bottomTextColor={bottomTextColor}
			bottomText={
				state === RUNNING &&
				isFreeWeSpin &&
				joined && <Caption className="u-text-white">{translations.WE_SPIN_YOU_ARE_REGISTERED}</Caption>
			}
			imageSrc={!hasEventTags && imageUrl}
			imageSrcSet={!hasEventTags && imageSrcSet}
			imageComponent={hasEventTags && <EventTypeTitle eventType={eventTags[0]} translations={translations} />}
		/>
	);
};

export default WeSpinGameRoomNextSessionItem;
